/**
 * The `feature` vocabulary of the GA `out_of_credits` event, and the one
 * mapping from the server's refusal key onto it. THIS FILE IS THE SOURCE OF
 * TRUTH: docs/plans/GA-INSTRUMENTATION.md lists the same values (a test
 * reads the doc's row and compares).
 *
 * One value per surface the user hit the wall on, the same from both emit
 * sites: the global upgrade prompt (stores/upgrade-prompt.store.ts) maps the
 * server's `details.feature` through gaCreditFeature(); a page's own
 * out-of-credits modal (OutOfTokensModal, a pre-check with no request) names
 * the page it is on. Before (review round 1 of #1066, findings 1 to 3) the
 * event carried the raw server cost key from one site and `misc_ai` /
 * `credits` from the other, so Empy alone reported under four labels.
 *
 * A key the server adds later lands in `misc_ai` until it is added here.
 */
export const GA_CREDIT_FEATURES = [
  'ai_tutor',
  'giraffe_translator',
  'needs_guesser',
  'echo_arena',
  'oral_quiz',
  'support_chat',
  'circles',
  'misc_ai',
] as const;

export type GaCreditFeature = (typeof GA_CREDIT_FEATURES)[number];

/**
 * Every key the server puts on a 402 insufficient_credits `details.feature`
 * (grep of server/app for the feature argument of CreditService::check /
 * checkAndConsume / consume and InsufficientCreditsException, 2026-10-24).
 * A Map, not an object literal, so an Object.prototype name such as
 * `constructor` cannot resolve to something that is not a vocabulary value.
 */
const BY_SERVER_KEY: ReadonlyMap<string, GaCreditFeature> = new Map([
  // Empy: the send pre-checks (text, audio) and the charge after the reply.
  ['empy_text_message', 'ai_tutor'],
  ['empy_audio_message', 'ai_tutor'],
  ['empy_conversation', 'ai_tutor'],
  ['giraffe_translator', 'giraffe_translator'],
  ['needs_guesser', 'needs_guesser'],
  // Echo Arena: Reflect and Roleplay (mediation).
  ['echo_arena_session', 'echo_arena'],
  ['roleplay_mediation_session', 'echo_arena'],
  ['oral_quiz_question', 'oral_quiz'],
  ['support_chat_message', 'support_chat'],
  // Live sessions: a circle join, and a 1-on-1 call's participant charge.
  ['circle_join', 'circles'],
  ['empathy_session_participant', 'circles'],
]);

/** The server's refusal key onto the GA vocabulary; anything else is misc_ai. */
export function gaCreditFeature(serverFeature: unknown): GaCreditFeature {
  return (typeof serverFeature === 'string' && BY_SERVER_KEY.get(serverFeature)) || 'misc_ai';
}
